import DateField from '~/components/fields/date-field.vue'
import InputField from '~/components/fields/input-field.vue'
import RadioField from '~/components/fields/radio-field.vue'
import TextareaField from '~/components/fields/textarea-field.vue'

import { z } from 'zod'

export function useStartupForm() {
  const { t } = useI18n()

  const component = {
    InputField: InputField,
    TextareaField: TextareaField,
    DateField: DateField,
    RadioField: RadioField,
  } as const

  const fields = {
    tip: {
      title: t('startup.form.sections.tip.title'),
      next: 'about',
      prev: null,
      description: t('startup.form.sections.tip.description'),
      fields: [
        {
          id: 'name',
          validation: z.string().nonempty(),
          component: component.InputField,
          props: {
            required: true,
            type: 'text',
            label: t('startup.form.name.label'),
            placeholder: t('startup.form.name.placeholder'),
          },
        },
        {
          id: 'company',
          validation: z.string().nonempty(),
          component: component.InputField,
          props: {
            required: true,
            label: t('startup.form.company.label'),
            placeholder: t('startup.form.company.placeholder'),
          },
        },
        {
          id: 'email',
          validation: z.string().email().nonempty(),
          component: component.InputField,
          props: {
            required: true,
            type: 'email',
            label: t('startup.form.email.label'),
            placeholder: t('startup.form.email.placeholder'),
          },
        },
        {
          id: 'website',
          validation: z.string().nonempty(),
          component: component.InputField,
          props: {
            required: true,
            label: t('startup.form.website.label'),
            placeholder: t('startup.form.website.placeholder'),
          },
        },
        {
          id: 'city',
          validation: z.string().nonempty(),
          component: component.InputField,
          props: {
            required: true,
            label: t('startup.form.city.label'),
            placeholder: t('startup.form.city.placeholder'),
          },
        },
        {
          id: 'socialMedia',
          validation: z.string().nonempty(),
          component: component.TextareaField,
          props: {
            required: true,
            label: t('startup.form.socialMedia.label'),
            placeholder: t('startup.form.socialMedia.placeholder'),
          },
        },
      ],
    },
    about: {
      title: t('startup.form.sections.about.title'),
      description: t('startup.form.sections.about.description'),
      next: 'business',
      prev: 'tip',
      fields: [
        {
          id: 'idea',
          validation: z.string().max(500).nonempty(),
          component: component.TextareaField,
          props: {
            label: t('startup.form.idea.label'),
            required: true,
          },
        },
        {
          id: 'currentTeam',
          validation: z.string().max(500).nonempty(),
          component: component.TextareaField,
          props: {
            label: t('startup.form.currentTeam.label'),
            required: true,
          },
        },
        {
          id: 'solve',
          validation: z.string().max(500).nonempty(),
          component: component.TextareaField,
          props: {
            label: t('startup.form.solve.label'),
            required: true,
          },
        },
        {
          id: 'similar',
          validation: z.string().max(500).nonempty(),
          component: component.TextareaField,
          props: {
            label: t('startup.form.similar.label'),
            required: true,
          },
        },
        {
          id: 'stage',
          validation: z.string().max(500).nonempty(),
          component: component.TextareaField,
          props: {
            required: true,
            label: t('startup.form.stage.label'),
          },
        },
        {
          id: 'projectType',
          validation: z.string().nonempty(),
          component: component.RadioField,
          props: {
            required: true,
            label: t('startup.form.projectType.label'),
            items: [
              {
                id: 'web',
                label: t('startup.form.projectType.options.web'),
                value: 'Web',
              },
              {
                id: 'mobile',
                label: t('startup.form.projectType.options.mobile'),
                value: 'Mobile',
              },
              {
                id: 'web-and-mobile',
                label: t('startup.form.projectType.options.web_and_mobile'),
                value: 'Web & Mobile',
              },
            ],
          },
        },
      ],
    },
    business: {
      title: t('startup.form.sections.business.title'),
      description: t('startup.form.sections.business.description'),
      next: null,
      prev: 'about',
      fields: [
        {
          id: 'money',
          validation: z.string().nonempty(),
          component: component.TextareaField,
          props: {
            required: true,
            label: t('startup.form.money.label'),
          },
        },
        {
          id: 'shares',
          validation: z.string().nonempty(),
          component: component.TextareaField,
          props: {
            required: true,
            label: t('startup.form.shares.label'),
          },
        },
        {
          id: 'budget',
          validation: z.string().nonempty(),
          component: component.TextareaField,
          props: {
            required: true,
            label: t('startup.form.budget.label'),
          },
        },
        {
          id: 'date',
          validation: z.string().nonempty(),
          component: component.DateField,
          props: {
            required: true,
            label: t('startup.form.date.label'),
            min: new Date().toISOString().split('T')[0],
            max: '2040-12-31',
          },
        },
      ],
    },
  } satisfies Record<string, FormGroup>

  return {
    fields,
  }
}

interface FormField {
  id: string
  validation: z.ZodType<any, any>
  component: any
  props: {
    label: string
    type?: string
    placeholder?: string
    charCount?: string
    required?: boolean
    items?: Array<{
      id: string
      label: string
      value: string
    }>
    min?: string
    max?: string
  }
}

interface FormGroup {
  title: string
  next: string | null
  prev: string | null
  description: string
  fields: FormField[]
}
